import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Inbox, LayoutList, Mail, Network, Plus, PlusCircle, Settings, Star, Trash2, Users } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { RoutingFlowCanvas } from '@/components/routing-flow/RoutingFlowCanvas'
import { DirectoryPanel } from '@/components/routing-flow/DirectoryPanel'
import {
  RULE_ACTIONS,
  TEAM_ROLES,
  summariseConditions,
  topLevelRules,
  type Mailbox,
  type RoutingRule,
  type RuleAction,
  type RuleConditions,
  type TeamRole,
} from '@/lib/email-rules'

type Action = RuleAction
type Role = TeamRole
type Rule = RoutingRule

// ── Mailbox filter type: 'all' shows every rule, 'global' shows null mailboxId, uuid shows that mailbox
type MailboxFilter = 'all' | 'global' | string

export function EmailRulesPage() {
  const qc = useQueryClient()
  const artistId = useAuthStore((s) => s.user?.artistId ?? '')
  const [viewMode, setViewMode] = useState<'visual' | 'list'>('visual')
  const [adding, setAdding] = useState(false)
  const [addingChildOf, setAddingChildOf] = useState<Rule | null>(null)
  const [editingRule, setEditingRule] = useState<Rule | null>(null)
  const [selectedMailbox, setSelectedMailbox] = useState<MailboxFilter>('all')
  const [showMailboxMgmt, setShowMailboxMgmt] = useState(false)
  const [showDirectory, setShowDirectory] = useState(false)

  // ── Fetch mailboxes ──
  const mailboxQuery = useQuery<Mailbox[], ApiClientError>({
    queryKey: ['mailboxes'],
    queryFn: () => artistFetch<Mailbox[]>('/mailboxes'),
  })
  const mailboxes = mailboxQuery.data ?? []

  // ── Fetch rules (filtered by selected mailbox) ──
  const rulesQueryKey = selectedMailbox === 'all'
    ? ['email-rules']
    : ['email-rules', selectedMailbox]

  const rulesEndpoint = selectedMailbox === 'all'
    ? '/email-rules'
    : `/email-rules?mailboxId=${selectedMailbox}`

  const { data, error, isLoading } = useQuery<Rule[], ApiClientError>({
    queryKey: rulesQueryKey,
    queryFn: () => artistFetch<Rule[]>(rulesEndpoint),
  })

  async function removeRule(id: string) {
    if (!confirm('Delete this rule?')) return
    try {
      await artistFetch(`/email-rules/${id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['email-rules'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  async function toggleEnabled(rule: Rule) {
    try {
      await artistFetch(`/email-rules/${rule.id}`, {
        method: 'PATCH',
        body: { enabled: !rule.enabled },
      })
      qc.invalidateQueries({ queryKey: ['email-rules'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  // Determine the mailboxId to assign to new rules
  const newRuleMailboxId: string | null =
    selectedMailbox === 'all' || selectedMailbox === 'global'
      ? null
      : selectedMailbox

  const rootRules = topLevelRules(data ?? [])

  return (
    <div>
      <PageHeader
        title="Intelligent routing"
        subtitle="Visual workflow: inbound feeds → AI analysis → branches and sub-filters → the right people."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-ovation-ink/15 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('visual')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'visual'
                    ? 'bg-ovation-ink text-ovation-paper'
                    : 'text-ovation-muted hover:text-ovation-ink'
                }`}
              >
                <Network className="h-3.5 w-3.5" />
                Visual
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'list'
                    ? 'bg-ovation-ink text-ovation-paper'
                    : 'text-ovation-muted hover:text-ovation-ink'
                }`}
              >
                <LayoutList className="h-3.5 w-3.5" />
                List
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowMailboxMgmt((v) => !v)}
              className="flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              <Settings className="h-4 w-4" />
              {showMailboxMgmt ? 'Hide mailboxes' : 'Mailboxes'}
            </button>
            <button
              type="button"
              onClick={() => setShowDirectory((v) => !v)}
              className="flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              <Users className="h-4 w-4" />
              People &amp; departments
            </button>
            {viewMode === 'list' && (
              <button
                type="button"
                onClick={() => { setAdding((a) => !a); setAddingChildOf(null) }}
                className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
              >
                <Plus className="h-4 w-4" />
                {adding && !addingChildOf ? 'Cancel' : 'Add rule'}
              </button>
            )}
          </div>
        }
      />

      {/* ── Mailbox management panel ── */}
      {showMailboxMgmt && (
        <MailboxManagement
          mailboxes={mailboxes}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ['mailboxes'] })
            qc.invalidateQueries({ queryKey: ['email-rules'] })
          }}
        />
      )}

      {/* ── Mailbox selector tabs ── */}
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-ovation-ink/10 pb-3">
        <MailboxTab
          label="All mailboxes"
          icon={<Inbox className="h-3.5 w-3.5" />}
          active={selectedMailbox === 'all'}
          onClick={() => setSelectedMailbox('all')}
        />
        <MailboxTab
          label="Global rules"
          icon={<Mail className="h-3.5 w-3.5" />}
          active={selectedMailbox === 'global'}
          onClick={() => setSelectedMailbox('global')}
          count={(data ?? []).filter((r) => r.mailboxId === null && r.parentRuleId === null).length}
        />
        {mailboxes.map((mb) => (
          <MailboxTab
            key={mb.id}
            label={mb.label}
            subtitle={mb.address}
            active={selectedMailbox === mb.id}
            onClick={() => setSelectedMailbox(mb.id)}
            count={mb._count?.rules}
            isDefault={mb.isDefault}
          />
        ))}
      </div>

      {viewMode === 'visual' && artistId && (
        <AsyncBoundary isLoading={isLoading} error={error}>
          <RoutingFlowCanvas
            artistId={artistId}
            mailboxFilter={selectedMailbox}
            mailboxes={mailboxes}
            rules={data ?? []}
            onRefresh={() => qc.invalidateQueries({ queryKey: rulesQueryKey })}
            showDirectory={showDirectory}
            onToggleDirectory={() => setShowDirectory((v) => !v)}
          />
        </AsyncBoundary>
      )}

      {viewMode === 'list' && (
        <DirectoryPanel open={showDirectory} onClose={() => setShowDirectory(false)} />
      )}

      {viewMode === 'list' && adding && !addingChildOf && (
        <AddRuleForm
          parentRuleId={null}
          mailboxId={newRuleMailboxId}
          allRules={data ?? []}
          mailboxes={mailboxes}
          onAdded={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['email-rules'] })
          }}
        />
      )}

      {viewMode === 'list' && addingChildOf && (
        <AddRuleForm
          parentRuleId={addingChildOf.id}
          mailboxId={addingChildOf.mailboxId ?? newRuleMailboxId}
          allRules={data ?? []}
          mailboxes={mailboxes}
          onAdded={() => {
            setAddingChildOf(null)
            qc.invalidateQueries({ queryKey: ['email-rules'] })
          }}
          onCancel={() => setAddingChildOf(null)}
        />
      )}

      {viewMode === 'list' && editingRule && (
        <EditRuleInline
          rule={editingRule}
          mailboxes={mailboxes}
          onSaved={() => {
            setEditingRule(null)
            qc.invalidateQueries({ queryKey: ['email-rules'] })
          }}
          onCancel={() => setEditingRule(null)}
        />
      )}

      {viewMode === 'list' && (
      <AsyncBoundary isLoading={isLoading} error={error}>
        {rootRules.length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            {selectedMailbox === 'all'
              ? 'No routing rules yet. Seed defaults created at install time, or add your own above.'
              : selectedMailbox === 'global'
                ? 'No global rules. Global rules apply to all mailboxes.'
                : `No rules for this mailbox. Rules created here only apply to emails received at ${mailboxes.find((m) => m.id === selectedMailbox)?.address ?? 'this address'}.`}
          </div>
        ) : (
          <div className="space-y-1">
            {rootRules.map((rule) => (
              <RuleTreeNode
                key={rule.id}
                rule={rule}
                depth={0}
                onEdit={setEditingRule}
                onDelete={removeRule}
                onToggle={toggleEnabled}
                onAddChild={(r) => { setAddingChildOf(r); setAdding(false) }}
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
      )}
    </div>
  )
}

function MailboxTab({
  label,
  subtitle,
  icon,
  active,
  onClick,
  count,
  isDefault,
}: {
  label: string
  subtitle?: string
  icon?: React.ReactNode
  active: boolean
  onClick: () => void
  count?: number | undefined
  isDefault?: boolean | undefined
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'bg-ovation-ink text-ovation-paper'
          : 'border border-ovation-ink/10 bg-white text-ovation-ink hover:bg-ovation-ink/5'
      }`}
    >
      {icon}
      <span className="font-medium">{label}</span>
      {subtitle && (
        <span className={`text-xs ${active ? 'text-ovation-paper/70' : 'text-ovation-muted'}`}>
          {subtitle}
        </span>
      )}
      {isDefault && (
        <Star className={`h-3 w-3 ${active ? 'text-yellow-300' : 'text-yellow-500'}`} />
      )}
      {count !== undefined && count > 0 && (
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
          active ? 'bg-ovation-paper/20 text-ovation-paper' : 'bg-ovation-ink/10 text-ovation-ink'
        }`}>
          {count}
        </span>
      )}
    </button>
  )
}

// ── Mailbox management section ──────────────────────────────────────────────

function MailboxManagement({
  mailboxes,
  onChanged,
}: {
  mailboxes: Mailbox[]
  onChanged: () => void
}) {
  const [newAddress, setNewAddress] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function addMailbox(e: FormEvent) {
    e.preventDefault()
    if (!newAddress.trim() || !newLabel.trim()) return
    setAdding(true)
    setError(null)
    try {
      await artistFetch('/mailboxes', {
        method: 'POST',
        body: {
          address: newAddress.trim(),
          label: newLabel.trim(),
          ...(newDesc.trim() ? { description: newDesc.trim() } : {}),
        },
      })
      setNewAddress('')
      setNewLabel('')
      setNewDesc('')
      onChanged()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to create mailbox')
    } finally {
      setAdding(false)
    }
  }

  async function deleteMailbox(id: string) {
    if (!confirm('Delete this mailbox? Its rules will become global.')) return
    try {
      await artistFetch(`/mailboxes/${id}`, { method: 'DELETE' })
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to delete mailbox')
    }
  }

  async function setDefault(id: string) {
    try {
      await artistFetch(`/mailboxes/${id}/set-default`, { method: 'POST' })
      onChanged()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to set default')
    }
  }

  return (
    <div className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h3 className="mb-3 font-display text-lg">Mailboxes</h3>
      <p className="mb-4 text-sm text-ovation-muted">
        Each mailbox has its own independent routing rules. The default mailbox is used when an incoming email does not match any configured address.
      </p>

      {/* Add form */}
      <form onSubmit={addMailbox} className="mb-4 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-ink/[0.02] p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Email address
            </label>
            <input
              type="email"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="bookings@example.com"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Label
            </label>
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Bookings"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              Description (optional)
            </label>
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="What this mailbox is for"
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
            />
          </div>
        </div>
        {error && (
          <div className="text-sm text-red-700">{error}</div>
        )}
        <button
          type="submit"
          disabled={adding || !newAddress.trim() || !newLabel.trim()}
          className="rounded-md bg-ovation-ink px-4 py-1.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {adding ? 'Adding...' : 'Add mailbox'}
        </button>
      </form>

      {/* List */}
      {mailboxes.length === 0 ? (
        <p className="text-sm text-ovation-muted">No mailboxes configured yet.</p>
      ) : (
        <div className="space-y-1">
          {mailboxes.map((mb) => (
            <div
              key={mb.id}
              className="flex items-center justify-between rounded-md border border-ovation-ink/10 px-4 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{mb.label}</span>
                  <code className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-xs font-mono text-ovation-muted">
                    {mb.address}
                  </code>
                  {mb.isDefault && (
                    <span className="flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                      <Star className="h-2.5 w-2.5" /> Default
                    </span>
                  )}
                  {!mb.isActive && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
                      Inactive
                    </span>
                  )}
                  <span className="text-xs text-ovation-muted">
                    {mb._count?.rules ?? 0} rule{(mb._count?.rules ?? 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                {mb.description && (
                  <p className="mt-0.5 text-xs text-ovation-muted truncate">{mb.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                {!mb.isDefault && (
                  <button
                    type="button"
                    onClick={() => setDefault(mb.id)}
                    title="Set as default"
                    className="rounded border border-ovation-ink/15 p-1 hover:bg-yellow-50 text-ovation-muted hover:text-yellow-600"
                  >
                    <Star className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => deleteMailbox(mb.id)}
                  className="rounded border border-ovation-ink/15 p-1 hover:bg-red-50 text-ovation-muted hover:text-red-600"
                  title="Delete mailbox"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Recursively renders a rule and its children as an expandable tree. */
function RuleTreeNode({
  rule,
  depth,
  onEdit,
  onDelete,
  onToggle,
  onAddChild,
}: {
  rule: Rule
  depth: number
  onEdit: (r: Rule) => void
  onDelete: (id: string) => void
  onToggle: (r: Rule) => void
  onAddChild: (r: Rule) => void
}) {
  const [expanded, setExpanded] = useState(depth === 0)
  const childCount = rule.children?.length ?? 0
  const hasChildren = childCount > 0

  const mailboxLabel = rule.mailbox?.label ?? (rule.mailboxId ? 'Unknown' : 'Global')

  return (
    <div>
      <div
        className="flex items-center gap-2 rounded-lg border border-ovation-ink/10 bg-white px-4 py-3 text-sm"
        style={{ marginLeft: `${depth * 28}px` }}
      >
        {/* Expand/collapse toggle */}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="shrink-0 text-ovation-muted hover:text-ovation-ink"
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          ) : (
            <span className="inline-block h-4 w-4" />
          )}
        </button>

        {/* Priority */}
        <span className="shrink-0 w-10 text-xs text-ovation-muted text-center">
          {rule.priority}
        </span>

        {/* Name + description */}
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{rule.name}</div>
          {rule.description && (
            <div className="text-xs text-ovation-muted truncate">{rule.description}</div>
          )}
        </div>

        {/* Mailbox badge */}
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
          rule.mailboxId
            ? 'bg-blue-100 text-blue-800'
            : 'bg-ovation-ink/5 text-ovation-muted'
        }`}>
          {mailboxLabel}
        </span>

        {/* Conditions summary */}
        <div className="hidden md:block text-xs text-ovation-muted max-w-[200px] truncate">
          {summariseConditions(rule.conditions)}
        </div>

        {/* Action */}
        <div className="text-xs shrink-0">
          <span className="font-medium">{rule.action.replace(/_/g, ' ')}</span>
          {rule.targetRole && <> &rarr; {rule.targetRole.replace(/_/g, ' ')}</>}
          {rule.targetEmail && <> &rarr; {rule.targetEmail}</>}
        </div>

        {/* Children count badge */}
        {hasChildren && (
          <span className="shrink-0 rounded-full bg-ovation-ink/10 px-2 py-0.5 text-[10px] font-medium text-ovation-ink">
            {childCount} child{childCount !== 1 ? 'ren' : ''}
          </span>
        )}

        {/* Enabled toggle */}
        <button
          type="button"
          onClick={() => onToggle(rule)}
          className={rule.enabled ? 'text-xs text-emerald-700' : 'text-xs text-ovation-muted'}
        >
          {rule.enabled ? 'On' : 'Off'}
        </button>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onAddChild(rule)}
            title="Add child rule"
            className="text-ovation-muted hover:text-ovation-ink"
          >
            <PlusCircle className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onEdit(rule)}
            className="text-xs text-ovation-ink hover:underline"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(rule.id)}
            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div className="mt-1 space-y-1">
          {rule.children!.map((child) => (
            <RuleTreeNode
              key={child.id}
              rule={child}
              depth={depth + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AddRuleForm({
  parentRuleId,
  mailboxId,
  allRules,
  mailboxes,
  onAdded,
  onCancel,
}: {
  parentRuleId: string | null
  mailboxId: string | null
  allRules: Rule[]
  mailboxes: Mailbox[]
  onAdded: () => void
  onCancel?: () => void
}) {
  const [name, setName] = useState('')
  const [priority, setPriority] = useState(100)
  const [action, setAction] = useState<Action>('forward_to_role')
  const [targetRole, setTargetRole] = useState<Role>('tour_manager')
  const [targetEmail, setTargetEmail] = useState('')
  const [classificationType, setClassificationType] = useState('')
  const [fromDomain, setFromDomain] = useState('')
  const [subjectContains, setSubjectContains] = useState('')
  const [bodyContains, setBodyContains] = useState('')
  const [selectedParent, setSelectedParent] = useState<string>(parentRuleId ?? '')
  const [selectedMailboxId, setSelectedMailboxId] = useState<string>(mailboxId ?? '')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const parentRule = allRules.find((r) => r.id === (parentRuleId ?? selectedParent))

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const conditions: RuleConditions = {}
      if (classificationType) conditions.classificationType = classificationType
      if (fromDomain) conditions.fromDomain = fromDomain
      if (subjectContains) conditions.subjectContains = subjectContains
      if (bodyContains) conditions.bodyContains = bodyContains

      const body: Record<string, unknown> = {
        name,
        priority,
        conditions,
        action,
        mailboxId: selectedMailboxId || null,
      }
      if (action === 'forward_to_role') body.targetRole = targetRole
      if (action === 'forward_to_email') body.targetEmail = targetEmail

      const effectiveParent = parentRuleId ?? (selectedParent || undefined)
      if (effectiveParent) body.parentRuleId = effectiveParent

      await artistFetch('/email-rules', { method: 'POST', body })
      onAdded()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  // Flatten rules for the parent selector (only show top-level and first-level)
  const flatRules = allRules.filter((r) => r.parentRuleId === null)

  return (
    <form
      onSubmit={onSubmit}
      className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6"
    >
      {parentRule && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700">
          Adding child rule under: <strong>{parentRule.name}</strong>
          {onCancel && (
            <button type="button" onClick={onCancel} className="ml-3 text-xs underline">
              Cancel
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Name</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            placeholder="e.g. Contracts from LiveNation"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Priority</label>
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Mailbox</label>
          <select
            value={selectedMailboxId}
            onChange={(e) => setSelectedMailboxId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">Global (all mailboxes)</option>
            {mailboxes.map((mb) => (
              <option key={mb.id} value={mb.id}>{mb.label} ({mb.address})</option>
            ))}
          </select>
        </div>
        {!parentRuleId && (
          <div>
            <label className="mb-1 block text-sm font-medium">Parent rule (optional)</label>
            <select
              value={selectedParent}
              onChange={(e) => setSelectedParent(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              <option value="">None (top-level)</option>
              {flatRules.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 text-xs uppercase tracking-wider text-ovation-muted">
          When (all must match)
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            placeholder="classification type (e.g. public_concert)"
            value={classificationType}
            onChange={(e) => setClassificationType(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
          <input
            placeholder="from domain (e.g. livenation.com)"
            value={fromDomain}
            onChange={(e) => setFromDomain(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
          <input
            placeholder="subject contains..."
            value={subjectContains}
            onChange={(e) => setSubjectContains(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
          <input
            placeholder="body contains..."
            value={bodyContains}
            onChange={(e) => setBodyContains(e.target.value)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <div className="mb-2 text-xs uppercase tracking-wider text-ovation-muted">
          Then
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as Action)}
            className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            {RULE_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          {action === 'forward_to_role' && (
            <select
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value as Role)}
              className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              {TEAM_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          )}
          {action === 'forward_to_email' && (
            <input
              type="email"
              placeholder="target email"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            />
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {submitting ? 'Creating...' : 'Create rule'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-sm text-ovation-muted hover:text-ovation-ink">
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

function EditRuleInline({
  rule,
  mailboxes,
  onSaved,
  onCancel,
}: {
  rule: Rule
  mailboxes: Mailbox[]
  onSaved: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(rule.name)
  const [priority, setPriority] = useState(rule.priority)
  const [action, setAction] = useState<Action>(rule.action)
  const [targetRole, setTargetRole] = useState<Role>(rule.targetRole ?? 'tour_manager')
  const [targetEmail, setTargetEmail] = useState(rule.targetEmail ?? '')
  const [selectedMailboxId, setSelectedMailboxId] = useState<string>(rule.mailboxId ?? '')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        name,
        priority,
        action,
        mailboxId: selectedMailboxId || null,
      }
      if (action === 'forward_to_role') body.targetRole = targetRole
      if (action === 'forward_to_email') body.targetEmail = targetEmail
      await artistFetch(`/email-rules/${rule.id}`, { method: 'PATCH', body })
      onSaved()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-blue-700">Editing: {rule.name}</div>
        <button type="button" onClick={onCancel} className="text-xs text-ovation-muted hover:text-ovation-ink">Cancel</button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Priority</label>
          <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Mailbox</label>
          <select
            value={selectedMailboxId}
            onChange={(e) => setSelectedMailboxId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">Global (all mailboxes)</option>
            {mailboxes.map((mb) => (
              <option key={mb.id} value={mb.id}>{mb.label} ({mb.address})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value as Action)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            {RULE_ACTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </div>
      {action === 'forward_to_role' && (
        <select value={targetRole} onChange={(e) => setTargetRole(e.target.value as Role)} className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
          {TEAM_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
        </select>
      )}
      {action === 'forward_to_email' && (
        <input type="email" value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)} className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="target email" />
      )}
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Saving...' : 'Save changes'}
      </button>
    </form>
  )
}
